import { ChangeDetectionStrategy, Component, Input, ViewEncapsulation } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { OrderingOption } from 'generated/graphql-types';
import { take } from 'rxjs/operators';
import { EntityRegisterView } from 'src/app/common-components/register/models/register-view';
import { TooltipService } from 'src/app/common-components/tooltip/tooltip-service';
import { PromptPrivileges } from '../models/prompt-privileges';
import { PromptRegisterOptions } from '../models/prompt-register-options';
import { PromptRegisterListItem } from '../models/prompt-register-results.model';
import { PromptTypeOption } from '../models/prompt-type-option.interface';
import { selectOrderingOptions, selectQuickFilters } from '../state/prompt-register.reducer';

@Component({
   changeDetection: ChangeDetectionStrategy.OnPush,
   encapsulation: ViewEncapsulation.None,
   selector: 'ndt-prompt-register-view',
   styleUrls: ['./prompt-register-view.component.scss'],
   templateUrl: './prompt-register-view.component.html',
   standalone: false,
})
export class PromptRegisterViewComponent extends EntityRegisterView<
   PromptRegisterListItem,
   PromptRegisterOptions,
   OrderingOption
> {
   protected selectQuickFilters(): (state: object) => PromptRegisterOptions | null {
      return selectQuickFilters;
   }
   protected override onQuickFiltersInit(quickFilters: PromptRegisterOptions): void {
      if (quickFilters) {
         this.selectedPromptType = quickFilters.typeName;
         this.includeInactive = quickFilters.includeInactivePrompts;
      }
   }
   constructor(private tooltipService: TooltipService, public store: Store) {
      super(store);
   }

   @Input() includeInactive: boolean;

   @Input() showExtensionRegisterPopup: boolean;

   @Input() promptPrivileges: PromptPrivileges;

   selectedPromptType: PromptTypeOption | undefined = undefined;

   isActionMenuHovered: boolean = false;

   ngOnDestroy() {
      this.tooltipService.destroyAllTooltips();
   }

   ngOnInit() {
      super.ngOnInit();
      this.store.pipe(select(selectOrderingOptions), take(1)).subscribe((orderingOptions) => {
         if (orderingOptions) {
            this.orderingOptions = orderingOptions;
         }
      });
   }
   @Input()
   getRouterLink(id: number) {
      return `${id}`;
   }
   onPromptTypeSelected(promptType: PromptTypeOption) {
      this.registerOptions = { ...this.registerOptions, typeName: promptType };
      this.quickFilterChange.emit(this.registerOptions);
   }

   buildMessage(id: number, type: string): string {
      let newMessage = `Not In Use \n`;
      const uniqueEntries = new Set<string>();
      const appendUnique = (entry: string) => {
         if (!entry || uniqueEntries.has(entry)) return;
         uniqueEntries.add(entry);
         newMessage += `\n ${entry}`;
      };
      this.dataSource.forEach((value) => {
         if (value.promptId === id) {
            if (value.autoAttendantConnection && value.autoAttendantConnection?.length > 0) {
               newMessage = `Used in Extensions: \n`;
               value.autoAttendantConnection.forEach((value) => {
                  if (value.extensionAutoAttendant) {
                     appendUnique(
                        `${value.extensionAutoAttendant?.extension.name} (${value.extensionAutoAttendant?.extension.number})`
                     );
                  }
               });
            }

            if (value.queueMusicOnHoldConnection && value.queueMusicOnHoldConnection?.length > 0) {
               newMessage = 'Used in Queue Extensions: \n';
               value.queueMusicOnHoldConnection.forEach((value) => {
                  if (value.extension) {
                     appendUnique(`${value.extension?.name} (${value.extension?.number})`);
                  }
               });
            }
            if (value.accountMusicOnHoldConnection) {
               value.accountMusicOnHoldConnection.forEach((value) => {
                  if (value.holdMusicPromptId && newMessage !== `Not In Use \n`) {
                     newMessage += 'Used as Account Music On Hold';
                  } else if (value.holdMusicPromptId) {
                     newMessage = 'Used as Account Music On Hold';
                  }
               });
            }

            if (value.closedExceptionPromptsConnection && value.closedExceptionPromptsConnection?.length > 0) {
               newMessage = 'Used in Schedules: \n';
               value.closedExceptionPromptsConnection.forEach((value) => {
                  if (value.schedule) {
                     appendUnique(`${value.schedule.name}`);
                  }
               });
            }

            if (value.queuePeriodicAnnouncementsConnection && value.queuePeriodicAnnouncementsConnection?.length > 0) {
               newMessage = 'Used in Queue Extensions: \n';
               value.queuePeriodicAnnouncementsConnection.forEach((value) => {
                  if (value.extensionQueue) {
                     appendUnique(`${value.extensionQueue.extension.name} (${value.extensionQueue.extension.number})`);
                  }
               });
            }

            if (value.queueCallbackPromptsConnection && value.queueCallbackPromptsConnection?.length > 0) {
               newMessage = 'Used in Queue Extensions: \n';
               value.queueCallbackPromptsConnection.forEach((value) => {
                  if (value.extension) {
                     appendUnique(`${value.extension?.name} (${value.extension?.number})`);
                  }
               });
            }

            if (
               (value.mailboxUnavailablePromptsConnection && value.mailboxUnavailablePromptsConnection?.length > 0) ||
               (value.mailboxBusyPromptsConnection && value.mailboxBusyPromptsConnection?.length > 0) ||
               (value.mailboxNamePromptsConnection && value.mailboxNamePromptsConnection?.length > 0) ||
               (value.mailboxTemporaryUnavailablePromptsConnection &&
                  value.mailboxTemporaryUnavailablePromptsConnection?.length > 0)
            ) {
               newMessage = 'Used in Mailboxes: \n';
               value.mailboxUnavailablePromptsConnection?.forEach((value) => {
                  if (value.extension) {
                     appendUnique(`${value.extension?.name} (${value.extension?.number})`);
                  }
               });
               value.mailboxBusyPromptsConnection?.forEach((value) => {
                  if (value.extension) {
                     appendUnique(`${value.extension?.name} (${value.extension?.number})`);
                  }
               });
               value.mailboxNamePromptsConnection?.forEach((value) => {
                  if (value.extension) {
                     appendUnique(`${value.extension?.name} (${value.extension?.number})`);
                  }
               });
               value.mailboxTemporaryUnavailablePromptsConnection?.forEach((value) => {
                  if (value.extension) {
                     appendUnique(`${value.extension?.name} (${value.extension?.number})`);
                  }
               });
            }
         }
      });
      return newMessage;
   }

   isPromptInUse(id: number, type: string): boolean {
      return this.dataSource.some((value) => {
         if (value.promptId !== id) return false;

         switch (type) {
            case 'AutoAttendant':
               return (value.autoAttendantConnection?.length ?? 0) > 0;

            case 'MusicOnHold':
               return (
                  (value.queueMusicOnHoldConnection?.length ?? 0) > 0 ||
                  (value.accountMusicOnHoldConnection?.some((conn) => !!conn.holdMusicPromptId) ?? false)
               );

            case 'ClosedException':
               return (value.closedExceptionPromptsConnection?.length ?? 0) > 0;

            case 'PeriodicAnnouncement':
               return (value.queuePeriodicAnnouncementsConnection?.length ?? 0) > 0;

            case 'QueueCallback':
               return (value.queueCallbackPromptsConnection?.length ?? 0) > 0;

            case 'VoiceMailGreeting':
               return (
                  (value.mailboxUnavailablePromptsConnection?.length ?? 0) > 0 ||
                  (value.mailboxNamePromptsConnection?.length ?? 0) > 0 ||
                  (value.mailboxBusyPromptsConnection?.length ?? 0) > 0 ||
                  (value.mailboxTemporaryUnavailablePromptsConnection?.length ?? 0) > 0
               );

            default:
               return false;
         }
      });
   }

   includeInactivePromptsChange(includeInactivePromptsSelected: boolean): void {
      this.registerOptions = { ...this.registerOptions, includeInactivePrompts: includeInactivePromptsSelected };
      this.quickFilterChange.emit(this.registerOptions);
   }
}
